<template>
  <div class="confirm-received-container">
    <!-- Header Section -->
    <div class="header-section">
      <h3 class="page-title">CHECK BOX ITEMS</h3>

      <!-- QR Code Input (when no products loaded) -->
      <div v-if="!products.length" class="input-section">
        <b-form-input
          v-model="code"
          v-on:keyup.enter="productsFromCode"
          type="text"
          ref="qr"
          name="qr"
          id="qr"
          size="lg"
          class="mobile-input"
          placeholder="SCAN QR CODE"></b-form-input>
      </div>

      <!-- Item Scanner Input (when products are loaded) -->
      <div v-else class="input-section">
        <div class="scanner-controls">
          <b-form-input
            v-model="scannedBox"
            v-on:keyup.enter="findScanned"
            type="text"
            name="scanned"
            id="scanned"
            ref="scanned"
            size="lg"
            class="mobile-input flex-grow-1"
            placeholder="SCAN ITEM"></b-form-input>
          <b-button
            @click="resetCheck"
            size="lg"
            class="reset-btn"
            variant="primary">
            NEW BOX QR
          </b-button>
        </div>

        <!-- Progress Summary -->
        <div class="progress-summary" v-if="products.length">
          <div class="summary-card">
            <span class="summary-label">Total Items:</span>
            <span class="summary-value">{{ totalItems }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Scanned:</span>
            <span class="summary-value">{{ totalScanned }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Progress:</span>
            <span class="summary-value">{{ progressPercentage }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-section">
      <b-icon class="text-dark" icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
    </div>

    <!-- Products List -->
    <div v-else-if="products.length && !loading" class="products-section">
      <!-- Desktop Table View -->
      <div class="d-none d-md-block table-wrapper">
        <table class="table table-hover" ref="articles">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">SKU</th>
              <th scope="col">NUM</th>
              <th scope="col">Name</th>
              <th scope="col" class="text-center">Count</th>
              <th scope="col" class="text-center">Confirmed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(val, i) of products"
                v-bind:key="i"
                :class="getRowClass(val)"
                :ref="'row-' + i">
              <td>{{ i + 1 }}</td>
              <td>{{ val.sku }}</td>
              <td>{{ val.ssku }}</td>
              <td>
                <div>{{ val.name }}</div>
                <div v-if="val.notes" class="item-note">
                  <b-icon icon="info-circle-fill" class="note-icon"></b-icon>
                  {{ val.notes }}
                </div>
              </td>
              <td class="text-center">{{ val.count || 0 }}</td>
              <td :class="[scannedIndex === i ? 'bg-info text-white' : '', 'text-center']">
                {{ val.scanned || 0 }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="d-md-none mobile-cards" ref="articles">
        <div
          v-for="(val, i) of products"
          :key="i"
          :class="['product-card', getCardClass(val), scannedIndex === i ? 'scanned-highlight' : '']"
          :ref="'row-' + i">
          <div class="card-header">
            <span class="item-number">#{{ i + 1 }}</span>
            <div class="scan-status">
              <span class="scan-count" :class="getScanCountClass(val)">
                {{ val.scanned || 0 }} / {{ val.count || 0 }}
              </span>
              <b-icon
                v-if="val.count === val.scanned"
                icon="check-circle-fill"
                class="success-icon"></b-icon>
              <b-icon
                v-else-if="val.count < val.scanned"
                icon="exclamation-triangle-fill"
                class="warning-icon"></b-icon>
            </div>
          </div>

          <div class="card-body">
            <h5 class="product-name">{{ val.name }}</h5>
            <div v-if="val.notes" class="item-note-mobile">
              <b-icon icon="info-circle-fill" class="note-icon"></b-icon>
              <span>{{ val.notes }}</span>
            </div>
            <div class="product-details">
              <div class="detail-row">
                <span class="detail-label">SKU:</span>
                <span class="detail-value">{{ val.sku }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">NUM:</span>
                <span class="detail-value">{{ val.ssku }}</span>
              </div>
            </div>
          </div>

          <div class="card-progress">
            <b-progress
              :value="val.scanned || 0"
              :max="val.count || 1"
              :variant="getProgressVariant(val)"
              height="8px"
              class="mb-0"></b-progress>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import moment from 'moment';
import fixed from '../../fixed';

const API_URL = `${fixed.API_URL}/admin`;

export default {
  name: 'confirm-received-items',
  data: () => ({
    loading: false,
    code: null,
    scannedIndex: null,
    scannedBox: '',
    products: [],
    focusInterval: null,
    successAudio: null,
    errorAudio: null,

    moment,
  }),
  computed: {
    totalItems() {
      return this.products.reduce((sum, p) => sum + (p.count || 0), 0);
    },
    totalScanned() {
      return this.products.reduce((sum, p) => sum + (p.scanned || 0), 0);
    },
    progressPercentage() {
      if (this.totalItems === 0) return 0;
      return Math.round((this.totalScanned / this.totalItems) * 100);
    },
  },
  watch: {
    products(newVal) {
      if (newVal.length) {
        this.focusInterval = setInterval(() => {
          this.$refs.scanned.focus();
        }, 1000);
      } else if (this.focusInterval) {
        clearInterval(this.focusInterval);
        this.focusInterval = null;
      }
    },
  },
  beforeDestroy() {
    if (this.focusInterval) {
      clearInterval(this.focusInterval);
    }
    // Clean up audio resources
    this.successAudio = null;
    this.errorAudio = null;
  },
  mounted() {
    this.$refs.qr.focus();
    // preload scan sounds
    this.initAudio();
  },
  methods: {
    initAudio() {
      if (!this.successAudio) {
        this.successAudio = new Audio('/ok.mp3');
        this.successAudio.preload = 'auto';
      }
      if (!this.errorAudio) {
        this.errorAudio = new Audio('/error.mp3');
        this.errorAudio.preload = 'auto';
      }
    },
    playSuccessSound() {
      if (this.successAudio) {
        this.successAudio.currentTime = 0;
        this.successAudio.play();
      }
    },
    playErrorSound() {
      if (this.errorAudio) {
        this.errorAudio.currentTime = 0;
        this.errorAudio.play();
      }
    },
    async productsFromCode() {
      if (!this.code) return;

      this.loading = true;
      try {
        const { data } = await axios.get(`${API_URL}/qr2articles?qr=${this.code}`, { withCredentials: true });
        if (!data || !data.length) {
          this.$toast.warning('No products for that code');
          this.loading = false;
          this.code = null;
          return;
        }
        // Filter out canceled items (noteType 4) and initialize scanned property in one pass
        this.products = data.reduce((acc, product) => {
          if (product.noteType !== 4) {
            acc.push({
              ...product,
              scanned: product.scanned || 0,
            });
          }
          return acc;
        }, []);
        if (!this.products.length) {
          this.$toast.warning('All items for that code are canceled');
        }
      } catch (err) {
        this.$toast.error(err.response ? err.response.data : 'can\t find products from that code');
      }
      this.code = null;
      this.loading = false;
    },
    findScanned() {
      if (!this.scannedBox) return;

      let fullyScannedIndex = -1;
      let foundIndex = -1;
      let shouldPlayError = false;

      for (let i = 0; i < this.products.length; i += 1) {
        const p = this.products[i];
        if (p.ean && p.ean.length && p.ean.includes(this.scannedBox)) {
          const scanned = p.scanned || 0;
          const { count } = p;

          // If this item is already fully scanned, save first occurrence and continue searching
          if (scanned >= count) {
            if (fullyScannedIndex === -1) {
              fullyScannedIndex = i;
            }
          } else {
            // Found an item that needs scanning - increment it
            const newScannedValue = scanned + 1;
            this.$set(this.products[i], 'scanned', newScannedValue);
            foundIndex = i;
            break;
          }
        }
      }

      // Determine which index to use and which sound to play
      if (foundIndex !== -1) {
        // Found an item that needed scanning
        this.scannedIndex = foundIndex;
        this.playSuccessSound();
      } else if (fullyScannedIndex !== -1) {
        // Only found fully scanned items - increment the saved one
        const newScannedValue = (this.products[fullyScannedIndex].scanned || 0) + 1;
        this.$set(this.products[fullyScannedIndex], 'scanned', newScannedValue);
        this.scannedIndex = fullyScannedIndex;
        shouldPlayError = true;
      } else {
        // No matching products found at all
        this.$toast.error('Has no EAN code or code not on the list');
        this.playErrorSound();
        this.scannedBox = '';
        return;
      }

      // Scroll article into the view
      this.$nextTick(() => {
        const element = this.$refs[`row-${this.scannedIndex}`];
        if (element) {
          const scrollTarget = Array.isArray(element) ? element[0] : element;
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      });

      if (shouldPlayError) {
        this.playErrorSound();
      }

      this.scannedBox = '';
    },
    resetCheck() {
      this.code = null;
      this.products = [];
      this.scannedIndex = null;
      this.$nextTick(() => {
        this.$refs.qr.focus();
      });
    },
    getRowClass(p) {
      if (p.count === p.scanned) return 'bg-success text-dark';
      if (p.count < p.scanned) return 'bg-warning text-dark';
      return '';
    },
    getCardClass(p) {
      if (p.count === p.scanned) {
        return 'card-success';
      } if (p.count < p.scanned) {
        return 'card-warning';
      }
      return '';
    },
    getScanCountClass(p) {
      if (p.count === p.scanned) {
        return 'count-success';
      } if (p.count < p.scanned) {
        return 'count-warning';
      }
      return 'count-pending';
    },
    getProgressVariant(p) {
      if (p.count < p.scanned) {
        return 'warning';
      } if (p.count === p.scanned) {
        return 'success';
      }
      return 'primary';
    },
  },
};
</script>
<style scoped>
/* Mobile-first Container */
.confirm-received-container {
  padding: 1rem;
  min-height: 100vh;
  background-color: transparent;
}

/* Header Section */
.header-section {
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem 1rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
  border-bottom: 3px solid var(--accent);
}

.page-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #ffffff;
  text-transform: uppercase;
}

/* Input Section */
.input-section {
  width: 100%;
}

.mobile-input {
  font-size: 1.1rem;
  padding: 0.75rem;
  border: 2px solid #495057;
  border-radius: 0.5rem;
  transition: border-color 0.15s ease-in-out;
  background-color: #212529;
  color: #fff;
}

.mobile-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  background-color: #212529;
  color: #fff;
}

.mobile-input::placeholder {
  color: #6c757d;
}

.scanner-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.reset-btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

/* Progress Summary */
.progress-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
}

.summary-card {
  background-color: #212529;
  padding: 0.75rem 0.5rem;
  border-radius: 0.375rem;
  text-align: center;
  border: 1px solid #495057;
}

.summary-label {
  display: block;
  font-size: 0.75rem;
  color: #adb5bd;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.summary-value {
  display: block;
  font-size: 1.25rem;
  font-weight: bold;
  color: #fff;
}

/* Loading Section */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  padding: 2rem;
}

.loading-section .text-dark {
  color: #fff !important;
}

/* Products Section */
.products-section {
  margin-top: 1rem;
}

/* Desktop Table */
.table-wrapper {
  background-color: #343a40;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.table {
  margin-bottom: 0;
  color: #fff;
}

.table thead th {
  background-color: #212529;
  color: #fff;
  border: none;
  padding: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
}

.table tbody tr {
  transition: background-color 0.15s ease-in-out;
  border-bottom: 1px solid #495057;
}

.table tbody tr:hover {
  background-color: #495057;
}

.table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-color: #495057;
}

/* Item Notes - Desktop */
.item-note {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-style: italic;
  background-color: rgba(0, 0, 0, 0.2);
}

.note-icon {
  flex-shrink: 0;
  font-size: 1rem;
}

/* Mobile Cards */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.product-card {
  background-color: #343a40;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border: 2px solid #495057;
}

.product-card:active {
  transform: scale(0.98);
}

.scanned-highlight {
  border-color: #17a2b8 !important;
  box-shadow: 0 0 0 0.25rem rgba(23, 162, 184, 0.5), 0 4px 12px rgba(0, 0, 0, 0.4) !important;
  animation: pulse 0.6s ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.card-success {
  background-color: #1e4620;
  border-color: #28a745;
}

.card-warning {
  background-color: #5a4a1f;
  border-color: #ffc107;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #212529;
  border-bottom: 1px solid #495057;
}

.item-number {
  font-weight: bold;
  font-size: 1rem;
  color: #adb5bd;
}

.scan-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.scan-count {
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.count-success {
  color: #28a745;
  background-color: #1e4620;
  border: 1px solid #28a745;
}

.count-warning {
  color: #ffc107;
  background-color: #5a4a1f;
  border: 1px solid #ffc107;
}

.count-pending {
  color: #17a2b8;
  background-color: #0d3a47;
  border: 1px solid #17a2b8;
}

.success-icon {
  color: #28a745;
  font-size: 1.25rem;
}

.warning-icon {
  color: #ffc107;
  font-size: 1.25rem;
}

.card-body {
  padding: 1rem;
}

.product-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #fff;
  line-height: 1.4;
}

/* Item Notes - Mobile */
.item-note-mobile {
  margin-bottom: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-style: italic;
  background-color: rgba(0, 0, 0, 0.2);
  line-height: 1.4;
}

.product-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: #212529;
  border-radius: 0.25rem;
}

.detail-label {
  font-weight: 600;
  color: #adb5bd;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.detail-value {
  font-weight: 500;
  color: #fff;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
}

.card-progress {
  padding: 0 1rem 1rem;
}

/* Tablet adjustments (sm breakpoint) */
@media (min-width: 576px) {
  .confirm-received-container {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .scanner-controls {
    flex-direction: row;
  }

  .reset-btn {
    width: auto;
    min-width: 180px;
  }

  .progress-summary {
    gap: 1rem;
  }

  .summary-card {
    padding: 1rem;
  }

  .summary-label {
    font-size: 0.875rem;
  }

  .product-name {
    font-size: 1.125rem;
  }
}

/* Desktop adjustments (md breakpoint) */
@media (min-width: 768px) {
  .confirm-received-container {
    padding: 2rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .header-section {
    padding: 2rem;
  }
}

/* Large desktop (lg breakpoint) */
@media (min-width: 992px) {
  .confirm-received-container {
    max-width: 1400px;
    margin: 0 auto;
  }
}
</style>
